/**
 * Sandbox Task & Checklist Catalog
 *
 * Code-defined task/checklist definitions shared between frontend and backend.
 * The legacy SANDBOX_TASKS is preserved for backward compat.
 * The new checklist system uses profile-driven, DAG-ordered items.
 *
 * @module types/sandbox-tasks
 */

// ---------------------------------------------------------------------------
// Artifact types
// ---------------------------------------------------------------------------

/** Artifact types with special platform behavior (template sync, file path mapping). */
export type WellKnownArtifactType =
  | 'docker-compose'
  | 'dockerfile'
  | 'startup-script'
  | 'setup-script'
  | 'seed-script'
  | 'environment-config';

/** Any string is a valid artifact type. Well-known types get special treatment. */
export type SandboxArtifactType = WellKnownArtifactType | (string & {});

// ---------------------------------------------------------------------------
// Legacy task types (backward compat)
// ---------------------------------------------------------------------------

export interface SandboxTaskDefinition {
  id: string;
  label: string;
  description: string;
  icon: string;
  producesArtifacts: SandboxArtifactType[];
  order: number;
  /** Prompt template ID in apps/api/src/ai/prompts/ */
  promptTemplate: string;
}

export interface SandboxTaskStatus {
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  summary?: string;
  error?: string;
}

/** A task definition merged with its runtime status and artifact info */
export interface SandboxTaskWithStatus extends SandboxTaskDefinition {
  taskStatus: SandboxTaskStatus;
  artifacts: Array<{
    artifactType: string;
    filename: string;
  }>;
}

/** Task SSE events are defined in conversation-stream.ts */
import type { TaskArtifactPublishedEvent, TaskCompletedEvent } from './conversation-stream.js';

export type TaskStreamEvent =
  | TaskArtifactPublishedEvent
  | TaskCompletedEvent;

export const SANDBOX_TASKS: SandboxTaskDefinition[] = [
  {
    id: 'setup-environment',
    label: 'Setup Environment',
    description: 'Create Docker Compose, Dockerfile, and startup script to run the app',
    icon: 'GearSix',
    producesArtifacts: ['docker-compose', 'dockerfile', 'startup-script', 'seed-script'],
    order: 1,
    promptTemplate: 'env-template-setup-environment',
  },
];

// ---------------------------------------------------------------------------
// Checklist system — profile-driven, DAG-ordered setup items
// ---------------------------------------------------------------------------

export type ChecklistItemId =
  | 'provisioned'
  | 'code-cloned'
  | 'environment-analyzed'
  | 'setup-script-created'
  | 'dockerfile-created'
  | 'docker-compose-created'
  | 'server-running'
  | 'server-accessible'
  | 'adhoc-test-ready'
  | 'seed-script-created'
  | 'unit-tests-runnable'
  | 'integration-tests-runnable'
  | 'template-verified';

export interface ChecklistItemDefinition {
  id: ChecklistItemId;
  label: string;
  description: string;
  dependsOn: ChecklistItemId[];
  producesArtifacts: SandboxArtifactType[];
  agentAutomatable: boolean;
  /** Whether this item must be completed/skipped before the template can flip to verified. */
  countsTowardVerification?: boolean;
  /** Prompt template ID in apps/api/src/ai/prompts/ (only for agent-automatable items) */
  promptTemplate?: string;
}

export type ChecklistProfile = 'minimal' | 'ci-ready' | 'full-stack';

export interface ChecklistProfileDefinition {
  id: ChecklistProfile;
  label: string;
  description: string;
  items: ChecklistItemId[];
}

export interface ChecklistItemStatus {
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  startedAt?: string;
  completedAt?: string;
  summary?: string;
  error?: string;
}

/** A checklist item definition merged with its runtime status and artifact info */
export interface ChecklistItemWithStatus extends ChecklistItemDefinition {
  itemStatus: ChecklistItemStatus;
  /** True when all deps are met and item is not yet completed/running/skipped */
  actionable: boolean;
  artifacts: Array<{ artifactType: string; filename: string }>;
}

// ---------------------------------------------------------------------------
// Checklist item definitions (DAG)
// ---------------------------------------------------------------------------

export const CHECKLIST_ITEMS: ChecklistItemDefinition[] = [
  {
    id: 'provisioned',
    label: 'Provisioned',
    description: 'Sandbox instance has been provisioned',
    dependsOn: [],
    producesArtifacts: [],
    agentAutomatable: false,
  },
  {
    id: 'code-cloned',
    label: 'Code Cloned',
    description: 'Repository has been cloned into the sandbox',
    dependsOn: ['provisioned'],
    producesArtifacts: [],
    agentAutomatable: false,
  },
  {
    id: 'environment-analyzed',
    label: 'Environment Analyzed',
    description: 'Repository analyzed for runtime, services, and dependencies',
    dependsOn: ['code-cloned'],
    producesArtifacts: ['environment-config'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-analyze-environment',
  },
  {
    id: 'setup-script-created',
    label: 'Setup Script Created',
    description: 'Environment setup script for installing dependencies and build prerequisites',
    dependsOn: ['environment-analyzed'],
    producesArtifacts: ['setup-script'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-create-setup-script',
  },
  {
    id: 'unit-tests-runnable',
    label: 'Unit Tests Runnable',
    description: 'Unit test suite can be executed successfully',
    dependsOn: ['setup-script-created'],
    producesArtifacts: [],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-verify-unit-tests',
  },
  {
    id: 'dockerfile-created',
    label: 'Dockerfile Created',
    description: 'Dockerfile generated from environment analysis',
    dependsOn: ['environment-analyzed'],
    producesArtifacts: ['dockerfile'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-create-dockerfile',
  },
  {
    id: 'docker-compose-created',
    label: 'Docker Compose Created',
    description: 'Docker Compose file with application and service containers',
    dependsOn: ['dockerfile-created'],
    producesArtifacts: ['docker-compose'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-create-docker-compose',
  },
  {
    id: 'integration-tests-runnable',
    label: 'Integration Tests Runnable',
    description: 'Integration tests can connect to services and run',
    dependsOn: ['docker-compose-created'],
    producesArtifacts: [],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-verify-integration-tests',
  },
  {
    id: 'seed-script-created',
    label: 'Seed Script Created',
    description: 'Database seed script for initial data population',
    dependsOn: ['docker-compose-created'],
    producesArtifacts: ['seed-script'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-create-seed-script',
  },
  {
    id: 'server-running',
    label: 'Server Running',
    description: 'Application server started via startup script',
    dependsOn: ['docker-compose-created'],
    producesArtifacts: ['startup-script'],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-start-server',
  },
  {
    id: 'server-accessible',
    label: 'Server Accessible',
    description: 'Application server responds to health check requests',
    dependsOn: ['server-running'],
    producesArtifacts: [],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-verify-server',
  },
  {
    id: 'adhoc-test-ready',
    label: 'Ad Hoc Test Ready',
    description:
      'Credential-producing sandbox action validated against a running instance for sandbox-backed ad hoc tests',
    dependsOn: ['server-accessible'],
    producesArtifacts: [],
    agentAutomatable: true,
    countsTowardVerification: false,
    promptTemplate: 'env-checklist-enable-adhoc-testing',
  },
  {
    id: 'template-verified',
    label: 'Template Verified',
    description: 'A fresh instance provisioned from this template comes up healthy',
    dependsOn: ['server-accessible'],
    producesArtifacts: [],
    agentAutomatable: true,
    promptTemplate: 'env-checklist-verify-template',
  },
];

/** O(1) lookup by checklist item ID */
export const CHECKLIST_ITEM_MAP: Record<ChecklistItemId, ChecklistItemDefinition> =
  Object.fromEntries(CHECKLIST_ITEMS.map((item) => [item.id, item])) as Record<
    ChecklistItemId,
    ChecklistItemDefinition
  >;

// ---------------------------------------------------------------------------
// Profile definitions
// ---------------------------------------------------------------------------

const ALL_CHECKLIST_ITEM_IDS: ChecklistItemId[] = CHECKLIST_ITEMS.map((item) => item.id);

export const CHECKLIST_PROFILES: Record<ChecklistProfile, ChecklistProfileDefinition> = {
  minimal: {
    id: 'minimal',
    label: 'Minimal',
    description: 'Provisioned sandbox with code cloned — git access only',
    items: ['provisioned', 'code-cloned'],
  },
  'ci-ready': {
    id: 'ci-ready',
    label: 'CI Ready',
    description: 'Code cloned with unit tests runnable — commit PRs and let CI handle the rest',
    items: ['provisioned', 'code-cloned', 'environment-analyzed', 'setup-script-created', 'unit-tests-runnable'],
  },
  'full-stack': {
    id: 'full-stack',
    label: 'Full Stack',
    description: 'Complete containerized environment with all services, tests, and server running',
    items: ALL_CHECKLIST_ITEM_IDS,
  },
};

export function getVerificationChecklistItemIds(profile: ChecklistProfile): ChecklistItemId[] {
  const profileItems = new Set(CHECKLIST_PROFILES[profile].items);
  return CHECKLIST_ITEMS.filter(
    (item) => profileItems.has(item.id) && item.countsTowardVerification !== false
  ).map((item) => item.id);
}
